import React, { useEffect } from 'react';

import './ProfileCompany.scss';
import { Col, Row, Table, Badge, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { getBilling, PlanPaymentDetail } from '../../../store/slices/companySlice';

const ProfileBillingCompany = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const billing = useSelector((state: RootState) => state.companyReducer.billing);

  useEffect(() => {
    dispatch(getBilling());
  }, [dispatch]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (expiryDate: string) => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getStatusBadge = (status: number) => {
    switch (status) {
      case 3: // CONFIRMED
        return <Badge bg="success">{t('billingPage.status.active')}</Badge>;
      case 6: // ERROR
        return <Badge bg="danger">{t('billingPage.status.error')}</Badge>;
      default:
        return <Badge bg="secondary">{t('billingPage.status.pending')}</Badge>;
    }
  };

  if (billing.loading) {
    return (
      <div className="zestur-card d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('billingPage.loading')}</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="zestur-card">
      <h2 className="general-heading mt-4 mb-4">{t('billingPage.title')}</h2>

      {/* Current Plan Section */}
      <div className="mb-5">
        <h4 className="mb-3">{t('billingPage.currentPlan.title')}</h4>
        {billing.current_plan ? (
          <div className="p-4 border rounded">
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <Badge bg="primary" className="fs-6 text-uppercase">
                    {billing.current_plan.plan_display}
                  </Badge>
                  {getStatusBadge(billing.current_plan.status)}
                </div>
                <p className="mb-1">
                  <strong>{t('billingPage.currentPlan.expires')}:</strong>{' '}
                  {formatDate(billing.current_plan.expiry_date)}
                </p>
                <p className="mb-0 text-muted">
                  {getDaysRemaining(billing.current_plan.expiry_date)}{' '}
                  {t('billingPage.currentPlan.daysRemaining')}
                </p>
              </Col>
              <Col md={4} className="text-md-end mt-3 mt-md-0">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => navigate('/company/profile/plans')}
                >
                  {t('billingPage.currentPlan.viewPlans')}
                </button>
              </Col>
            </Row>
          </div>
        ) : (
          <div className="p-4 border rounded bg-light">
            <p className="mb-3">{t('billingPage.currentPlan.noPlan')}</p>
            <button
              className="btn btn-primary primary-btn-color"
              onClick={() => navigate('/company/profile/plans')}
            >
              {t('billingPage.currentPlan.subscribeToPlan')}
            </button>
          </div>
        )}
      </div>

      {/* Payment History Section */}
      <div>
        <h4 className="mb-3">{t('billingPage.paymentHistory.title')}</h4>
        {billing.payment_history && billing.payment_history.length > 0 ? (
          <Table responsive hover>
            <thead>
              <tr>
                <th>{t('billingPage.paymentHistory.date')}</th>
                <th>{t('billingPage.paymentHistory.plan')}</th>
                <th>{t('billingPage.paymentHistory.status')}</th>
                <th>{t('billingPage.paymentHistory.invoice')}</th>
              </tr>
            </thead>
            <tbody>
              {billing.payment_history.map((payment: PlanPaymentDetail) => (
                <tr key={payment.id}>
                  <td>{formatDate(payment.created_at)}</td>
                  <td>{payment.plan_display}</td>
                  <td>{getStatusBadge(payment.status)}</td>
                  <td>
                    {payment.invoice_url && (
                       <a 
                         href={payment.invoice_url} 
                         target="_blank" 
                         rel="noopener noreferrer"
                         className="btn btn-sm btn-link p-0 text-decoration-none"
                       >
                         <i className="bi bi-download me-1"></i>
                         PDF
                       </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-muted">{t('billingPage.paymentHistory.noHistory')}</p>
        )}
      </div>
    </div>
  );
};

export default ProfileBillingCompany;
